@file:Suppress("UNCHECKED_CAST")

package nl.volleyplanner.web.base

import kotlinx.browser.document
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.IdGenerator
import nl.volleyplanner.web.Logger
import nl.volleyplanner.web.util.BootstrapColor
import org.w3c.dom.HTMLElement


interface SimpleComponent {
    fun dispose() { }

    /**
     * Invoked when the component has been added to the document
     */
    fun onReady() { }
    fun render(flowContent: FlowContent)
}

fun FlowContent.simpleComponent(simpleComponent: SimpleComponent) {
    try {
        simpleComponent.render(this)
    } catch (e: Exception) {
        println("Error while rendering simplecomponent ${simpleComponent::class}: ${e.message}")
        e.printStackTrace()
    }
    val controller = ControllerContext.getController()

    require(controller != null) { "Controller not found while rendering simpleComponent: ${simpleComponent::class}" }
    controller.registerSimpleComponent(simpleComponent)
}


// TODO: We are starting to see more sophisticated components requiring more needs
//  need to revamp the system to better work with parent/child components (and likely more)
fun FlowContent.component(component : HTMLComponent, parentComponent: HTMLComponent? = null) : HTMLElement {
    return if (parentComponent != null) {
        ComponentManager.createComponent(component, this.consumer as TagConsumer<HTMLElement>, parentComponent)
    } else {
        ComponentManager.createComponent(component, this.consumer as TagConsumer<HTMLElement>)
    }

}

@OptIn(FlowPreview::class)
object ComponentManager {
    private val logger = Logger.getLogger("nl.volleyplanner.web.base.ComponentManager")
    private val componentStack = mutableListOf<HTMLComponent>()
    private val componentParentMap = mutableMapOf<HTMLComponent, HTMLComponent>()
    private val parentComponentMap = mutableMapOf<HTMLComponent, MutableSet<HTMLComponent>>()

    private val refreshRequests = MutableStateFlow<Set<HTMLComponent>>(emptySet())

    init {
        CoroutineManager.DISPATCHER_SCOPE.launch {
            refreshRequests
                .debounce(20L)
                .collect({ refreshRequests ->
                    if (refreshRequests.isNotEmpty()) refreshComponents()
                })
        }
    }

    fun createComponent(
        component: HTMLComponent,
        tagConsumer: TagConsumer<HTMLElement>,
        parentComponent: HTMLComponent? = componentStack.lastOrNull()
    ) : HTMLElement {
        logger.debug("[${parentComponent?.logIdentifier}/${component.logIdentifier}] createComponent: ${component::class}")
        if (parentComponent != null) {
            componentParentMap[component] = parentComponent
            parentComponentMap.getValue(parentComponent).add(component)
        }
        parentComponentMap[component] = mutableSetOf()
        componentStack.add(component)
        val result = component.initialize(tagConsumer)
        componentStack.remove(component)
        return result
    }

    fun removeComponent(component: HTMLComponent) : List<HTMLComponent> {
        if (componentParentMap.containsKey(component) || parentComponentMap.containsKey(component)) {
            componentParentMap.remove(component)
            val childrenToRemove = parentComponentMap.remove(component)
            val removedChildren = childrenToRemove?.map { removeComponent(it) }?.flatten() ?: emptyList()
            return listOf(component) + removedChildren
        }
        return emptyList()
    }

    fun disposeChildren(component: HTMLComponent) : List<HTMLComponent> {
        val componentsToRemove = parentComponentMap[component]?.map { it }
        return componentsToRemove?.map { removeComponent(it) }?.flatten() ?: emptyList()
    }

    fun requestRefresh(component: HTMLComponent) {
        refreshRequests.value = refreshRequests.value + component
    }

    fun anyParentHasRefreshRequest(batch: Set<HTMLComponent>, component: HTMLComponent): Boolean {
        val parent = componentParentMap[component]
        return parent != null && (refreshRequests.value.contains(parent) || anyParentHasRefreshRequest(batch, parent))
    }

    suspend fun refreshComponents() {
        val pendingRefreshRequests = refreshRequests.value
        refreshRequests.value = emptySet()

        logger.debug("Call to refreshComponents: ${pendingRefreshRequests.size} requests, ${componentParentMap.size} parents")
        val componentsToRefresh = pendingRefreshRequests.filter { component ->
            parentComponentMap.containsKey(component) && !anyParentHasRefreshRequest(pendingRefreshRequests, component)
        }

        logger.debug {
            var componentLogIdentifiers = componentsToRefresh
                .distinctBy { it.clazz.simpleName }
                .take(10)
                .joinToString(", ") { it.logIdentifier }
            if (componentsToRefresh.size > 5) componentLogIdentifiers += ", ..."
            "Refreshing ${pendingRefreshRequests.size} refresh requests -> ${componentsToRefresh.size} (parent) components: $componentLogIdentifiers"
        }
        componentsToRefresh.forEach { component ->
            componentStack.add(component)
            component.replaceContent()
            require(componentStack.removeLast() == component)
        }
    }
}

/**
 * Lifecycle:
 *
 * initialize(consumer)
 *  component.render()
 *  ComponentManager.requestRefresh()
 *    async -> replaceContent()
 *          children -> component.dispose()
 *          component.render()
 *          component.onContentReplaced()
 *  component.onReady()
 */
abstract class HTMLComponent {
    companion object {
        private val logger = Logger.getLogger("nl.volleyplanner.web.base.HTMLComponent")
    }

    /**
     * Coroutine scope for the lifecycle of this component
     * The scope is automatically cancelled when the component is disposed
     */
    val componentScope = CoroutineScope(CoroutineManager.RENDER_SCOPE.coroutineContext + SupervisorJob() + CoroutineManager.coroutineExceptionHandler)

    val componentId = IdGenerator.nextId()
    val clazz = this::class
    val logIdentifier = "${clazz.simpleName}@$componentId"

    // TODO: merge _coroutineJob with componentScope ?
    private var _coroutineJob : Job? = null
    private var _controller: Controller? = null
    val owningController: Controller
        get() = _controller ?: throw IllegalStateException("[$logIdentifier] Component is not yet initialized")

    private var workingElement : HTMLElement? = null

    /**
     * Called after the render() function has been called, but BEFORE the resulting element has been added to the DOM
     */
    open fun onReady() { }

    /**
     * Called when a new state value has been received
     */
    open fun onStateChange() { }
    /**
     * Called after a render() run
     */
    open fun onContentReplaced(htmlElement: HTMLElement) { }
    open fun onDispose() {}
    // TODO: Pass state to the render function, so it is always available when doing flow transformations
    abstract fun render(consumer: TagConsumer<HTMLElement>) : HTMLElement
    abstract suspend fun getState() : StateFlow<*>?

    suspend fun <T : StandaloneHTMLComponent> createStandaloneComponent(component: T) : T {
        logger.debug("[${logIdentifier}] ${this::class}.createStandaloneComponent: ${component::class}@${component.componentId}")
        if (!owningController.hasComponent(component)) {
            val _this = this
            withContext(Dispatchers.Main) {
                owningController.registerComponent(component)
                ControllerContext.withController(owningController) {
                    logger.debug("[${logIdentifier}] ${this::class}.createStandaloneComponent using controller: ${owningController.name}")
                    //val modalElement = component.initialize(document.create)
                    val modalElement = ComponentManager.createComponent(component, document.create, _this)
                    val containerElement = component.getContainerElement() ?: document.body
                    containerElement?.append(modalElement)
                    component.initialize()
                }
            }
        }

        return component
    }

    fun initialize(consumer: TagConsumer<HTMLElement>) : HTMLElement {
        _controller = ControllerContext.getController()
        logger.trace("[$logIdentifier] Initializing component ${this::class}, controller: ${owningController.name}")
        owningController.registerComponent(this)

        val element = try {
            render(consumer)
        } catch (e: Exception) {
            println("[${logIdentifier}] Error while rendering component: ${e.message}")
            e.printStackTrace()
            consumer.div { +"Error" }
        }
        workingElement = element
        logger.debug("[$logIdentifier] Initializing component, element = [tag=${element.tagName}, id=${element.id}, classes=${element::class}]")
        val _this = this
        _coroutineJob = componentScope.launch {
            logger.trace("[$logIdentifier] State current value: ${getState()?.value}")
            getState()?.collect { newState ->
                ComponentManager.requestRefresh(_this)
            }
        }

        // onReady can initialize API requests etc. But HTML content is not rendered yet
        onReady()
        return element
    }

    suspend fun replaceContent() {
        val _this = this
        withContext(Dispatchers.Main) {
            ControllerContext.withController(owningController) {
                try {
                    logger.trace { "[$logIdentifier] Received replaceContent(), element = [tag=${workingElement!!.tagName}, id=${workingElement!!.id}, classes=${workingElement!!.className}]" }
                    if (!isDisposed()) {
                        val disposedChildren = ComponentManager.disposeChildren(_this)
                        logger.trace { "[$logIdentifier] Got disposed children: ${disposedChildren.size}" }
                        disposedChildren.forEach { it.dispose() }
                        logger.trace { "[$logIdentifier] disposed children: ${disposedChildren.size}" }
                        val newElement = render(document.create)
                        logger.trace { "[$logIdentifier] Replace workingElement [tag=${workingElement!!.tagName}, id=${workingElement!!.id}, classes=${workingElement!!.className}, isConnected=${workingElement!!.isConnected}] with newElement [tag=${newElement.tagName}, id=${newElement.id}, classes=${newElement.className}]" }
                        workingElement!!.replaceWith(newElement)
                        workingElement = newElement
                        owningController.initializeSimpleComponents()
                        onContentReplaced(newElement)
                    } else {
                        logger.trace("[$logIdentifier] Component is already disposed, not replacing content")
                    }
                } catch (e: CancellationException) {
                    logger.info { "[$logIdentifier] CancellationException while rendering component: class = ${_this::class}, id = ${_this.componentId}. Exception: ${e.message}" }
                    throw e
                } catch (e: Exception) {
                    println("[$logIdentifier] Error when rendering component: class = ${_this::class}, id = ${_this.componentId}. Exception: ${e.message}")
                    e.printStackTrace()
                }
            }
        }
    }

    fun isDisposed() = _controller == null || !_controller!!.hasComponent(this)

    fun dispose() {
        logger.trace { "[$logIdentifier] dispose component" }
        _coroutineJob?.cancel()
        logger.trace("[$logIdentifier] unregister component")
        owningController.unregisterComponent(this)
        logger.trace("[$logIdentifier] remove component")
        ComponentManager.removeComponent(this)
        componentScope.cancel()
        logger.trace("[$logIdentifier] onDispose")
        onDispose()
        logger.debug("[$logIdentifier] disposed component")
    }
}

abstract class StandaloneHTMLComponent : HTMLComponent() {
    /**
     * Called after the component has been appended to the HTML for the first time
     * Can be used to instantiate anything requiring the elements to be present in the DOM
     */
    abstract fun initialize()

    open fun getContainerElement() : HTMLElement? = null
}

enum class SpinnerStyle(val cssClass: String) {
    Border("spinner-border"),
    Grow("spinner-grow")
}

enum class IconPosition {
    Start,
    End
}

fun FlowContent.icon(name: String, iconPosition: IconPosition = IconPosition.Start, text: String? = null,
                     color: BootstrapColor? = null, useMargin: Boolean = true, extraCssClasses: Set<String> = emptySet()) {
    val meClass = when (iconPosition) {
        IconPosition.Start -> "me-1"
        IconPosition.End -> "ms-1"
    }
    when {
        name.startsWith("vp") -> span(classes = "vp-icon $name") {
            classes += extraCssClasses
            if (text != null) {
                if (useMargin) {
                    classes += meClass
                }
                span(classes = "visually-hidden") {+text }
            }
        }
        name.startsWith("bi") -> i(classes = "bi $name") {
            classes += extraCssClasses
            if (color != null) {
                classes += color.textColorClass
            }
            if (text != null) {
                if (useMargin) {
                    classes += meClass
                }
                span(classes = "visually-hidden") {+text }
            }
        }
    }
}

fun FlowContent.spinner(style: SpinnerStyle = SpinnerStyle.Border, small: Boolean = false) {
    val addedClasses = if (small) " ${style.cssClass}-sm" else ""

    return div(classes = "ms-1 ${style.cssClass}${addedClasses}") {
        role = "status"
        span(classes = "visually-hidden") { +"Loading data..." }
    }
}